import type { MarginInventoryLatestEntry, MarginTerm } from "@yutai/shared"
import type { ThisMonthInventoryRow } from "./thisMonthInventory.js"

// 直近アップロードと1つ前のスナップショットの差分、およびX投稿文の生成（純粋関数）。
// 差分は /margin-inventory/latest の prevQuantity から導けるので、リロードしても同じ内容が出る。

export const TWEET_LIMIT = 140
const MAX_TWEET_LINES = 12

export interface DiffItem {
  code: string
  name: string
  /** 直近の在庫。null = 直近リストに載っていない（取扱なし） */
  quantity: number | null
  /** 1つ前の在庫。null = 前回リストに載っていなかった */
  prevQuantity: number | null
  /** quantity - prevQuantity（どちらかが null のときは null 扱いを 0 として計算） */
  delta: number
}

export interface TermDiff {
  term: MarginTerm
  snapshotDate: string
  prevSnapshotDate: string | null
  /** 変化のあった銘柄（変化量の絶対値が大きい順） */
  changed: DiffItem[]
  /** 変化のなかった銘柄のうち在庫ありのもの（在庫の多い順） */
  unchangedInStock: DiffItem[]
  unchangedCount: number
}

/** 何回前のアップロードまで遡って差分を見られるか（0 = 直近 vs 1つ前、4 = 5回前 vs 6回前） */
export const MAX_DIFF_BACK = 4

interface SnapshotPair {
  snapshotDate: string
  quantity: number | null
  prevSnapshotDate: string | null
  prevQuantity: number | null
}

function pairAt(e: MarginInventoryLatestEntry, back: number): SnapshotPair | null {
  if (back === 0) {
    return {
      snapshotDate: e.snapshotDate,
      quantity: e.quantity,
      prevSnapshotDate: e.prevSnapshotDate,
      prevQuantity: e.prevQuantity,
    }
  }
  const cur = e.recent?.[back]
  if (!cur) return null
  const prev = e.recent[back + 1]
  return {
    snapshotDate: cur.date,
    quantity: cur.quantity,
    prevSnapshotDate: prev?.date ?? null,
    prevQuantity: prev?.quantity ?? null,
  }
}

/** back 回前のアップロードとその1つ前の差分（back = 0 が直近） */
export function buildTermDiff(
  rows: ThisMonthInventoryRow[],
  term: MarginTerm,
  back = 0
): TermDiff | null {
  let snapshotDate: string | null = null
  let prevSnapshotDate: string | null = null
  const changed: DiffItem[] = []
  const unchangedInStock: DiffItem[] = []
  let unchangedCount = 0

  for (const row of rows) {
    const raw = row.inventory[term]
    const e = raw && pairAt(raw, back)
    if (!e) continue
    snapshotDate = e.snapshotDate
    prevSnapshotDate = e.prevSnapshotDate
    // 前回スナップショットが無い（初回アップロード）なら差分は出さない
    if (!e.prevSnapshotDate) continue
    const item: DiffItem = {
      code: row.code,
      name: row.name,
      quantity: e.quantity,
      prevQuantity: e.prevQuantity,
      delta: (e.quantity ?? 0) - (e.prevQuantity ?? 0),
    }
    if (e.quantity === null && e.prevQuantity === null) continue
    if (item.delta !== 0 || (e.quantity === null) !== (e.prevQuantity === null)) {
      changed.push(item)
    } else {
      unchangedCount++
      if ((e.quantity ?? 0) > 0) unchangedInStock.push(item)
    }
  }
  if (!snapshotDate) return null
  changed.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || a.code.localeCompare(b.code))
  unchangedInStock.sort((a, b) => (b.quantity ?? 0) - (a.quantity ?? 0))
  return { term, snapshotDate, prevSnapshotDate, changed, unchangedInStock, unchangedCount }
}

/** 変化の表記: 減少 "5↓" / 増加 "5↑" / 新規・取扱終了 */
export function formatDelta(item: DiffItem): string {
  if (item.prevQuantity === null) return "新規"
  if (item.quantity === null) return "取扱終了"
  return item.delta < 0
    ? `${(-item.delta).toLocaleString("ja-JP")}↓`
    : `${item.delta.toLocaleString("ja-JP")}↑`
}

function formatQuantity(q: number | null): string {
  return q === null ? "—" : q.toLocaleString("ja-JP")
}

/** "A株式会社（定款上の商号　B株式会社）" → "B株式会社"（定款上の商号があればそちらを優先） */
export function preferredName(name: string): string {
  const m = name.match(/[（(]定款上の商号[\s\u3000:：]*([^）)]+)[）)]/)
  return m?.[1] ? m[1].trim() : name
}

function tweetLine(item: DiffItem | (DiffItem & { noDelta: true })): string {
  const name = preferredName(item.name)
  if ("noDelta" in item) return `${name}: ${formatQuantity(item.quantity)}`
  // 取扱終了（リストから消えた）は在庫0として表記し、「取扱終了」とは書かない
  if (item.quantity === null) return `${name}: 0 (${item.prevQuantity?.toLocaleString("ja-JP")}↓)`
  return `${name}: ${formatQuantity(item.quantity)} (${formatDelta(item)})`
}

/** 一言コメント（減った量が多ければ強め、変化なしならその旨） */
export function tweetComment(diff: TermDiff): string {
  if (diff.changed.length === 0) return "前回から変わらず、在庫は動いていません。"
  let dec = 0
  let inc = 0
  let prevTotal = 0
  for (const i of diff.changed) {
    if (i.delta < 0) dec += -i.delta
    else inc += i.delta
    prevTotal += i.prevQuantity ?? 0
  }
  if (dec === 0) return "在庫が増えました。"
  if (inc > 0) return "増減まちまち、動きのある一日でした。"
  const ratio = prevTotal > 0 ? dec / prevTotal : 0
  if (ratio >= 0.5) return "かなり減りました！お早めに。"
  if (ratio >= 0.2) return "結構減りました。"
  return "少し減りました。"
}

function pack(header: string, lines: string[], limit: number): string[] {
  const chunks: string[] = []
  let cur: string[] = []
  const render = (ls: string[], first: boolean) =>
    `${first ? header : "（続き）"}\n\n${ls.join("\n")}`
  for (const line of lines) {
    const next = [...cur, line]
    if (cur.length > 0 && Array.from(render(next, chunks.length === 0)).length > limit) {
      chunks.push(render(cur, chunks.length === 0))
      cur = [line]
    } else {
      cur = next
    }
  }
  if (cur.length > 0) chunks.push(render(cur, chunks.length === 0))
  return chunks
}

/** X投稿文。1ツイート（140字）に収まらなければ複数ツイートに分割する */
export function buildTweets(diff: TermDiff, month: number): string[] {
  const header = `今日の${month}月優待クロス在庫は...`
  const comment = tweetComment(diff)
  const items: (DiffItem | (DiffItem & { noDelta: true }))[] =
    diff.changed.length > 0
      ? diff.changed.slice(0, MAX_TWEET_LINES)
      : diff.unchangedInStock
          .slice(0, MAX_TWEET_LINES)
          .map((i) => ({ ...i, noDelta: true as const }))
  const lines = items.map(tweetLine)

  const withComment = (chunks: string[]): string[] => {
    const last = chunks[chunks.length - 1] ?? header
    const joined = `${last}\n\n${comment}`
    if (Array.from(joined).length <= TWEET_LIMIT) return [...chunks.slice(0, -1), joined]
    return [...chunks, comment]
  }

  let tweets = withComment(pack(header, lines, TWEET_LIMIT))
  if (tweets.length > 1) {
    // 「(1/3)」の連番ぶんを見込んで詰め直す
    const reserve = 6
    tweets = withComment(pack(header, lines, TWEET_LIMIT - reserve))
    tweets = tweets.map((t, i) => `${t}\n(${i + 1}/${tweets.length})`)
  }
  return tweets
}
